import { useSyncExternalStore } from 'react'
import type { ReactElement } from 'react'
import { MyLocationButton } from '@metro/route-card'
import { useGeolocation } from '../state/geolocationContext'
import { useMetroMapStore } from '../state/useMetroMapStore'
import { BREAKPOINT_PX } from './StationCardOrDetailsRouter'
import styles from './MyLocationFloatingButton.module.css'

/**
 * TASK-391 §3.3 — floating "my location" button for tablet / desktop
 * (regular width, ≥ {@link BREAKPOINT_PX}). On those form factors the
 * button is REMOVED from the RouteCard side panel (see
 * `StationCardOrDetailsRouter`) and floated over the map bottom-right with
 * 16/16 insets instead (user-requested 2026-06-18). Mirrors the iOS
 * `_myLocationFloatingButton` overlay.
 *
 * Same {@link MyLocationButton} + same `onMyLocation` logic the compact
 * (phone) card uses, and it shares the `isResolvingMyLocation` spinner
 * state. Hidden on compact (the phone overlay keeps the in-card button)
 * and while a route is being built (collapsed-only, mirroring the gate the
 * card applies). Mounted once in `AppShell`.
 */
export function MyLocationFloatingButton(): ReactElement | null {
  // Regular width only — the phone (compact) overlay keeps the in-card
  // button, so on compact this float renders nothing.
  const isRegularWidth = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false,
  )

  const geolocation = useGeolocation()
  const store = useMetroMapStore()
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)

  // Collapsed-only — while a route is being built (loading / results /
  // empty / error) the side panel owns the surface; the float hides
  // (mirrors the iOS `_routeStatus.kind == .collapsed` gate).
  if (!isRegularWidth || geolocation === null) return null
  if (routeStatus.kind !== 'collapsed') return null

  return (
    <div className={styles.floating} data-testid="my-location-floating">
      <MyLocationButton
        variant="tablet"
        onClick={geolocation.onMyLocation}
        isLoading={geolocation.isResolvingMyLocation}
      />
    </div>
  )
}
